import type React from 'react';

import { StaticHeadersForm } from '@/components/form/StaticHeadersForm';
import {
  COMPLEX_LOGIN_FORM_INITIAL,
  type ComplexLoginFormData,
  CredentialFormBody,
  CredentialType,
  IdentitySetManager,
  validateComplexLoginMulti,
} from '@/sections/asset/components/tabs/credentials/credentialForms';
import type {
  SeedStepProps,
  WizardCredentialData,
} from '@/sections/seeds/components/modals/AddSeedByTypeModal/AddSeedByTypeModal.types';

import { collectFieldErrors } from './credentialStep.util';
import { useConfigureCredential } from './useConfigureCredential';

// Stable no-op setter for CredentialFormBody's required (but unused for
// non-COMPLEX types) complexLoginForm prop. COMPLEX_LOGIN renders
// IdentitySetManager instead, so this is never invoked — mirrors Host A
// (AuthenticationTab's noopSetComplexLoginForm).
const noopSetComplexLoginForm: React.Dispatch<
  React.SetStateAction<ComplexLoginFormData>
> = () => {};

/**
 * CredentialFormStep — the wizard's credential FORM step. Shown only for web
 * seeds when the user chose "Configure" (gated via shouldSkip in wizardConfig),
 * immediately after the CredentialApproachStep cards. Renders ONLY the chosen
 * approach's form (STATIC_TOKEN → StaticHeadersForm, else → CredentialFormBody)
 * plus the inline FE validation errors — no approach cards here. The RECIPE path
 * is handled separately: it skips this step and runs the recipe builder as the
 * parent wizard's own recipe sub-steps (see wizardConfig + index.tsx). Fully
 * controlled into `formData.credential.data` via the shared
 * {@link useConfigureCredential} hook (no local secret state — security P2-2).
 * The inline FE validation errors come from the shared {@link collectFieldErrors}
 * (in credentialStep.util), the same helper the wizard step `validate` uses to
 * gate Next/Finish — so what the user sees and what blocks submission stay in sync.
 */
export function CredentialFormStep({ formData, setFormData }: SeedStepProps) {
  const state = useConfigureCredential(formData, setFormData);
  // Defensive: shouldSkip already gates this to configure mode, but bail if a
  // stray render reaches it on the skip path.
  if (state.data === null) return null;

  const { data, updateData, makeSetter, firstSeedUrl } = state;

  // Complex-Login is a SINGLE screen here (Host B Phase 4): the shared
  // IdentitySetManager master/detail, fanning out N identities over the single
  // web-app seed. Mirrors the asset drawer (AuthenticationTab). validation is
  // recomputed each render (React Compiler memoizes — no useMemo per
  // ui/CLAUDE.md) and drives both the inline per-identity errors and the
  // "N of M ready" line.
  if (data.selectedType === CredentialType.COMPLEX_LOGIN) {
    const validation = validateComplexLoginMulti(data.complexLogin);
    return (
      <div className="flex flex-1 flex-col gap-4">
        <IdentitySetManager
          value={data.complexLogin}
          onChange={next => updateData({ complexLogin: next })}
          webAppUrl={firstSeedUrl}
          validation={validation}
          // Surface per-identity errors live as the user edits — Add Seeds is
          // already disabled until valid, so a deferred "attempted" gate adds no
          // value (YAGNI). Matches the wizard's existing inline-error behavior.
          attempted
        />
        <span className="text-chariot-text-secondary text-sm">
          {validation.readyCount} of {data.complexLogin.identities.length} ready
        </span>
      </div>
    );
  }

  const handleStaticHeadersChange = (
    headers: WizardCredentialData['staticTokenForm']['headers']
  ) => updateData({ staticTokenForm: { ...data.staticTokenForm, headers } });

  // FE validation guards surfaced inline (P1-4 defense-in-depth). Header rows
  // are validated against the current controlled values; the backend still
  // independently sanitizes. (COMPLEX_LOGIN returned above.)
  const fieldErrors = collectFieldErrors(data);

  return (
    <div className="flex flex-1 flex-col justify-center gap-4">
      <div className="border-chariot-stroke rounded-lg border p-4">
        {data.selectedType === CredentialType.STATIC_TOKEN ? (
          <StaticHeadersForm
            secret
            label={data.staticTokenForm.label}
            onLabelChange={label =>
              updateData({
                staticTokenForm: { ...data.staticTokenForm, label },
              })
            }
            headers={data.staticTokenForm.headers}
            onHeadersChange={handleStaticHeadersChange}
          />
        ) : (
          <CredentialFormBody
            selectedType={data.selectedType}
            basicForm={data.basicForm}
            setBasicForm={makeSetter('basicForm')}
            recordingForm={data.recordingForm}
            setRecordingForm={makeSetter('recordingForm')}
            bearerTokenForm={data.bearerTokenForm}
            setBearerTokenForm={makeSetter('bearerTokenForm')}
            apiKeyForm={data.apiKeyForm}
            setApiKeyForm={makeSetter('apiKeyForm')}
            // COMPLEX_LOGIN renders IdentitySetManager above and never reaches
            // this branch, but CredentialFormBody still requires these props. A
            // stable placeholder + no-op setter satisfies the type without any
            // complexLoginForm state (mirrors Host A's AuthenticationTab).
            complexLoginForm={COMPLEX_LOGIN_FORM_INITIAL}
            setComplexLoginForm={noopSetComplexLoginForm}
          />
        )}
      </div>

      {fieldErrors.length > 0 && (
        <ul className="space-y-1" aria-live="polite">
          {fieldErrors.map(message => (
            <li key={message} className="text-error text-xs">
              {message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
